import {createContext, ReactNode, useCallback, useContext, useEffect, useState} from "react";
import jwt from 'jwt-decode';
import {api} from '../services/Api';
import {getCurrentDate} from "../Utils/currentDate";
import {useNotifications} from "./NotificationsContext";

type UserProps = {
	cdoc: string;
	cid: number;
	cname: string;
	ctopic: [[{
		conexoes: [];
		contratos: [];
		faturas: [];
		geral: [];
	}]];
	database: string;
	exp: number;
	iat: number;
	iss: number;
	uid: number;
	ulogin: string;
	uname: string;
}

/**
 * Versao da sessao gravada no localStorage. Ao carregar, uma sessao com versao diferente e descartada e o
 * cliente volta para o login. Subir este numero em um deploy derruba todas as sessoes do portal de uma vez,
 * sem mexer na API (e sem afetar o app).
 */
export const SESSAO_VERSAO = 2;

type SessionProps = {
	versao?: number;
	token: string;
	date_access: string;
	data: UserProps;
	exp: number;
	id_contract: string;
	notifications_invoices: boolean,
	notifications_contracts: boolean,
	notifications_connections: boolean,
	numero_contrato: string;
}

export type Canal = 'sms' | 'whatsapp' | 'email';

/**
 * Resposta do /login quando a API pede o segundo fator (status 2): o token só vem depois que o cliente
 * confirma, em /login/verificar-codigo, o código enviado pelo canal que ele escolheu (SMS, WhatsApp ou e-mail).
 */
export type ChallengeProps = {
	/** null enquanto o codigo ainda nao foi enviado (pendente = true): o cliente precisa escolher o canal e tocar em Enviar */
	desafio: string | null;
	pendente?: boolean;
	/** canal pelo qual o codigo foi enviado (so depois do envio) */
	canal?: Canal;
	/** destino mascarado do canal usado (ou do SMS, antes do envio) */
	destino: string | null;
	/** canais disponiveis para este cadastro, cada um com o destino mascarado; null = indisponivel */
	canais?: { sms: string | null; whatsapp: string | null; email: string | null };
	expira_em: number;
}

export function isChallenge(resultado: SessionProps | ChallengeProps): resultado is ChallengeProps {
	return (resultado as ChallengeProps).desafio !== undefined;
}

type AuthContextData = {
	session: SessionProps | null;
	handleSetSession: (data: SessionProps | null) => void;
	/** enviar = true pede o envio do codigo pelo canal (sms, whatsapp ou email); sem ele a API so devolve o aviso com os canais */
	signIn(document: string, phone: string, local: string | null, enviar?: boolean, canal?: Canal): Promise<SessionProps | ChallengeProps>;
	verifyCode(desafio: string, codigo: string, lembrar: boolean, local: string | null): Promise<SessionProps>;
	logOut: ()=> void;
}

type AuthContextProviderProps = {
	children: ReactNode;
}

/** Token do navegador lembrado (dispensa o código por LOGIN_2FA_DISPOSITIVO_DIAS); só o hash fica na API. */
const DISPOSITIVO_KEY = 'session_netcomet_dispositivo';

export const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export function AuthContextProvider(props: AuthContextProviderProps){
	const [session, setSession] = useState<SessionProps | null>(null);

	const {subscribe, unsubscribe} = useNotifications();

	/** Monta a sessão a partir dos dados devolvidos pela API (login direto ou após o código). */
	const concluirLogin = useCallback((dados: any, local: string | null): SessionProps => {
		let infoUser: UserProps = jwt(dados.token);
		let data: SessionProps = {
			versao: SESSAO_VERSAO,
			token: dados.token,
			date_access: getCurrentDate(),
			notifications_invoices: true,
			notifications_contracts: true,
			notifications_connections: true,
			data: infoUser,
			exp: infoUser.exp,
			id_contract: '',
			numero_contrato: ''
		};

		if (dados.dispositivo) {
			try {
				localStorage.setItem(DISPOSITIVO_KEY, dados.dispositivo);
			} catch (e) {
			}
		}

		if (!('Notification' in window) || !('serviceWorker' in navigator)) {
			localStorage.setItem('session_netcomet_topics_all', '');
			console.log('Notificações push não suportadas neste navegador.');
		} else {
			if (local !== null) {
				Notification.requestPermission().then(function (permission) {
					if (permission === 'granted') {
						let topics = []
						for (let i in infoUser.ctopic) {
							for (let j in infoUser.ctopic[i]) {
								topics.push(infoUser.ctopic[i][j]);
							}
						}
						subscribe(topics, data.token);
						localStorage.setItem('session_netcomet_topics_all', JSON.stringify(infoUser.ctopic));
					}
				});
			}
		}

		localStorage.setItem('session_netcomet', JSON.stringify(data));
		setSession(data);
		return data;
	}, [subscribe]);

	const signIn = useCallback( (document: string, phone: string, local: string | null = null, enviar?: boolean, canal?: Canal): Promise<SessionProps | ChallengeProps> => {
		localStorage.removeItem('session_netcomet');

		return new Promise<SessionProps | ChallengeProps>((resolve, reject) => {
			let dispositivo: string | null = null;
			try {
				dispositivo = localStorage.getItem(DISPOSITIVO_KEY);
			} catch (e) {
			}

			api.post('/login',{
				documento: document,
				telefone: phone,
				codigo: process.env.REACT_APP_SUPERGER_API_CODIGO,
				dispositivo: dispositivo || undefined,
				enviar: enviar ? true : undefined,
				canal: enviar ? (canal || 'sms') : undefined,
			}).then(result => {
				//segundo fator: a API enviou o código e devolveu o desafio
				if (result.data.status === 2) {
					resolve(result.data.dados as ChallengeProps);
					return;
				}
				resolve(concluirLogin(result.data.dados, local));
			}).catch(error => {
				reject(error?.response?.data?.message || 'Não foi possível entrar. Tente novamente.');
			})
		})
	}, [concluirLogin])

	const verifyCode = useCallback((desafio: string, codigo: string, lembrar: boolean, local: string | null = null): Promise<SessionProps> => {
		return new Promise<SessionProps>((resolve, reject) => {
			api.post('/login/verificar-codigo', {
				codigo: process.env.REACT_APP_SUPERGER_API_CODIGO,
				desafio: desafio,
				codigo_verificacao: codigo,
				lembrar_dispositivo: lembrar,
				nome_dispositivo: navigator.userAgent,
			}).then(result => {
				if (result.data.status !== 1) {
					reject(result.data.message || 'Código inválido');
					return;
				}
				resolve(concluirLogin(result.data.dados, local));
			}).catch(error => {
				reject(error?.response?.data?.message || 'Não foi possível validar o código. Tente novamente.');
			})
		})
	}, [concluirLogin])

	const logOut = useCallback(async () => {
		//o JWT ainda e necessario para remover os topicos via API; le do storage porque o state pode estar defasado
		let jwtAtual: string | null = null;
		try {
			jwtAtual = JSON.parse(localStorage.getItem('session_netcomet') || 'null')?.token || null;
		} catch (e) {
			jwtAtual = null;
		}
		unsubscribe(jwtAtual);
		setSession(null);
		localStorage.removeItem('session_netcomet');
		localStorage.removeItem('session_netcomet_token');
		localStorage.removeItem('session_netcomet_topics');
		//o dispositivo lembrado fica: sair da conta nao e "esquecer este navegador"
	}, [unsubscribe])

	useEffect(() => {
		let dataSession : any;
		dataSession = localStorage.getItem('session_netcomet');
		dataSession = JSON.parse(dataSession);
		if (dataSession) {
			let date = new Date().getTime()/1000;
			//sessao de um bundle anterior (sem versao ou com versao diferente) ou token vencido: sai e volta ao login
			if(dataSession.exp < date || dataSession.versao !== SESSAO_VERSAO){
				logOut().then(() => window.location.href = '/');
			} else {
				setSession(dataSession);
			}
		}
	}, [logOut]);

	const handleSetSession = useCallback( (data: SessionProps | null) => {
		setSession(data);
	},[]);

	const value = {
		session,
		handleSetSession,
		signIn,
		verifyCode,
		logOut
	}

	return(
		<AuthContext.Provider value={value}>
			{props.children}
		</AuthContext.Provider>
	);
}

export function useAuth(){
	return useContext(AuthContext);
}
